import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSessionPersistence } from '../hooks/useSessionPersistence';
import FlowResumeFrameWrapper from './FlowResumeFrameWrapper';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { session } = useSessionPersistence();
  const location = useLocation();
  const { hasAccess, isLoading, error } = useSubscriptionStatus();

  // Check authentication from both session state AND localStorage.
  // This helps cover brief state-sync gaps after login/logout.
  const checkAuthentication = (): boolean => {
    if (session.accountToken || session.accountId || session.email) {
      return true;
    }

    try {
      const token = localStorage.getItem('account-token');
      return !!token;
    } catch (e) {
      console.error('[ProtectedRoute] Error reading localStorage:', e);
      return false;
    }
  };

  const isAuthenticated = checkAuthentication();

  useEffect(() => {
    if (!isAuthenticated) {
      console.log('[ProtectedRoute] Unauthenticated access attempt to:', location.pathname);
    }
  }, [isAuthenticated, location.pathname]);
  
    // Routes that should remain reachable without an active subscription
  const isSubscriptionExempt =
    location.pathname.includes('/frame-250') ||
    location.pathname.includes('/estate-selector') ||
    location.pathname.includes('/executor-coming-soon') ||
    location.pathname.includes('/executor/') ||
    location.pathname.includes('/executor-plan/') ||
    location.pathname.includes('/settlement/') ||
    location.pathname.includes('/settings') ||
    location.pathname.includes('/beneficiary-coming-soon');

  // First priority: expired / missing auth should ALWAYS go to login.
  // This prevents timeout/logout flows from being hijacked by billing redirects.
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // At this point, the user is authenticated.
  // Only then should subscription gating even be considered.
  //
  // While subscription is still loading, do not redirect to billing yet.
  // Avoid false redirects during logout / token transition / initial fetch.
  if (!isSubscriptionExempt && isLoading) {
    return null;
  }

  // If subscription lookup failed, do not force-send the user to billing.
  // Let the authenticated route render rather than creating a bad redirect loop.
  if (!isSubscriptionExempt && error) {
    console.error('[ProtectedRoute] Subscription check failed:', error);
    return (
      <FlowResumeFrameWrapper>
        {children}
      </FlowResumeFrameWrapper>
    );
  }

  // Only redirect to Frame 250 when:
  // 1. user is definitely authenticated
  // 2. route is not exempt
  // 3. subscription check finished successfully
  // 4. backend says they do not have access
  if (!isSubscriptionExempt && !hasAccess) {
    return <Navigate to="/frame-250" replace />;
  }

  return (
    <FlowResumeFrameWrapper>
      {children}
    </FlowResumeFrameWrapper>
  );
};

export default ProtectedRoute;